import React from 'react';

import { cn } from '../helpers/cn';

import { DROPDOWN_PANEL_CLASSNAME } from './dropdown-colors';

/**
 * The floating surface a dropdown drops down — and nothing else.
 *
 * It exists because the *panels* across this workspace are all the same thing while the *triggers*
 * are not: a location picker, an info menu, a shop menu, a burger menu and a sorting menu look
 * nothing alike at rest, but the sheet each of them opens is one surface with one hairline, and
 * every one of them used to hardcode `bg-white` and get it wrong on a dark-themed domain.
 *
 * Deliberately not positioned. Where a panel opens is the caller's business — some are anchored by
 * Headless UI, some are `absolute` next to their trigger — and trying to own that here is what
 * would push every caller back to overriding it. Radius is the caller's too, since the menus use
 * `rounded-8` and the select-style dropdowns `rounded-4`; a radius set here would fight those
 * depending on stylesheet order rather than losing to them predictably.
 *
 * `as` takes whatever element hosts the panel, so a Headless UI `MenuItems` or `PopoverPanel` can
 * use this without giving up its own props:
 *
 * ```tsx
 * <DropdownPanel as={MenuItems} anchor="bottom end" modal={false} className="rounded-8 z-dropdown">
 * ```
 *
 * `overflow-hidden` is included: every current caller clips its rows to the panel's rounded
 * corners. A panel that has to scroll should not use this component — the select-style dropdowns
 * take `DROPDOWN_PANEL_CLASSNAME` directly for exactly that reason.
 */
type DropdownPanelProps<T extends React.ElementType> = {
    as?: T;
    className?: string;
    children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'className' | 'children'>;

function DropdownPanel<T extends React.ElementType = 'div'>({
    as,
    className,
    children,
    ...rest
}: DropdownPanelProps<T>): React.JSX.Element {
    // Cast to a component type rather than the bare `React.ElementType`: since @react-three/fiber's
    // module augmentation (three-types.d.ts) extends the global JSX.IntrinsicElements with every three.js
    // tag, `React.ElementType` widens to that whole union and its `children`/`className` collapse to
    // `never` wherever both a DOM tag and those intrinsics are in scope.
    const Component = (as ?? 'div') as React.ComponentType<
        { className?: string; children?: React.ReactNode } & Record<string, unknown>
    >;
    return (
        <Component
            className={cn(DROPDOWN_PANEL_CLASSNAME, 'overflow-hidden', className)}
            {...rest}>
            {children}
        </Component>
    );
}

export default DropdownPanel;
